import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, LayoutDashboard, BrainCircuit, LandPlot, HeartPulse,
  Trophy, MessagesSquare, LogOut, CalendarClock, BookOpen, Briefcase, ClipboardList, Radio, Timer, UploadCloud, Video,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Command Deck", icon: LayoutDashboard, id: "dashboard" },
  { to: "/super-brain", label: "Super Brain", icon: BrainCircuit, id: "super-brain" },
  { to: "/advisor", label: "Formation Advisor", icon: LandPlot, id: "advisor" },
  { to: "/matchday", label: "Matchday Plan", icon: ClipboardList, id: "matchday" },
  { to: "/live", label: "Live Match Mode", icon: Timer, id: "live" },
  { to: "/live-video", label: "Live Video Co-Pilot", icon: Video, id: "live-video" },
  { to: "/briefing", label: "Manager Briefing", icon: Briefcase, id: "briefing" },
  { to: "/fixtures", label: "Upcoming Fixtures", icon: CalendarClock, id: "fixtures" },
  { to: "/health", label: "Fitness & Mindset", icon: HeartPulse, id: "health" },
  { to: "/codex", label: "Tactics Codex", icon: BookOpen, id: "codex" },
  { to: "/leagues", label: "Training Grounds", icon: Trophy, id: "leagues" },
  { to: "/ingest", label: "Data Ingest", icon: UploadCloud, id: "ingest" },
  { to: "/hive", label: "Fleet Feed", icon: Radio, id: "hive" },
  { to: "/console", label: "OCTON Console", icon: MessagesSquare, id: "console" },
];

export const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <>
      <button
        data-testid="burger-menu-btn"
        onClick={() => setOpen(true)}
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 18px)", left: "calc(env(safe-area-inset-left, 0px) + 20px)" }}
        className="fixed z-40 flex h-11 w-11 items-center justify-center rounded-xl glass text-white/90 transition hover:border-cyan-400/40 hover:text-cyan-300"
      >
        <Menu size={20} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
              data-testid="sidebar-overlay"
            />
            <motion.aside
              initial={{ x: -340 }} animate={{ x: 0 }} exit={{ x: -340 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="fixed left-0 top-0 z-50 flex h-full w-[300px] flex-col glass"
              data-testid="sidebar-panel"
            >
              <div className="flex items-center justify-between px-6 pt-6">
                <div className="flex items-center gap-3">
                  <img src="/octon-logo.png" alt="OCTON" className="h-11 w-11 drop-shadow-[0_0_12px_rgba(0,229,255,0.5)]" />
                  <div>
                    <p className="font-tac text-[10px] tracking-[0.25em] txt-cyan">AVA FLEET · SHIP 07</p>
                    <h1 className="font-head text-2xl font-black leading-none tracking-tighter text-white">OCTON</h1>
                    <p className="text-[11px] text-white/40">Coach Companion</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="sidebar-close" className="text-white/60 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <nav className="mt-8 flex flex-1 flex-col gap-1 px-4">
                {NAV.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`nav-${item.id}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition ${
                        isActive
                          ? "bg-white/[0.06] text-white border border-cyan-400/30"
                          : "text-white/55 hover:bg-white/[0.03] hover:text-white border border-transparent"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon size={18} className={isActive ? "txt-cyan" : ""} />
                        <span className="font-body">{item.label}</span>
                        {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-cyan-400 glow-cyan" />}
                      </>
                    )}
                  </NavLink>
                ))}
              </nav>

              <div className="border-t border-white/5 p-4">
                <div className="mb-3 flex items-center gap-3">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-white/10" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600" />
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm text-white">{user?.name || "Coach"}</p>
                    <p className="truncate text-[11px] text-white/40">{user?.email}</p>
                  </div>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 py-2.5 text-sm text-white/70 transition hover:border-red-500/40 hover:text-red-400"
                >
                  <LogOut size={16} /> Disconnect
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
